import type { HandlerMap } from '../dispatch';
import { enqueueSendToNative } from '../transport';
import { useSceneSettingsStore } from '@/store/sceneSettingsStore';
import type { ShotMetadataOption } from './shotSettings';

export interface SceneSettingsValues {
  sceneNumber: string;
  sceneName: string;
  sceneNotes: string;
  locationType: string;
  timeType: string;
  lightingMode: string;
  postProcessingKey: string;
  floorType: string;
}
export interface SceneSettingsOption { value: string; label: string }
export interface SceneSettingsPayloadData {
  sceneId: number;
  values: SceneSettingsValues;
  options: {
    locationTypes: SceneSettingsOption[];
    timeTypes: SceneSettingsOption[];
    postProcessing: ShotMetadataOption[];
    floor: ShotMetadataOption[];
  };
  flags: { numberingLocked: boolean };
  error: { field: string; messageKey: string } | null;
  // Additive: Unity always emits it, but older echoes/tests may omit it.
  audioClipCount?: number;
}

export interface SetSceneSettingsMessage {
  type: 'setSceneSettings';
  payload: SceneSettingsPayloadData;
}
export interface FloorPreviewPayloadData {
  floorType: string;
  imageUrl: string;
}
export interface SetFloorPreviewMessage {
  type: 'setFloorPreview';
  payload: FloorPreviewPayloadData;
}

/** Sky (scene-lighting) preview render — the Floor pair's twin, with one difference that drives the
 *  whole shape: Unity renders the sky swatch from the scene's CURRENT lighting state, not from a
 *  discrete catalog value, so there is no single "type" to key a cache by. The echo therefore
 *  restates every input that went into the render (locationType/timeType/lightingMode/floorType for
 *  the given sceneId) and the web caches under the composite of all five — see skyPreviewKey. */
export interface SkyPreviewPayloadData {
  sceneId: number;
  locationType: string;
  timeType: string;
  lightingMode: string;
  /** The floor Unity actually rendered under the sky — always the scene record's PERSISTED floor
   *  (SendSkyPreviewToWebView passes rec.GetSceneFloorType()); the request carries no floor
   *  candidate, so a not-yet-applied floor pick never reaches this render. */
  floorType: string;
  imageUrl: string;
}
export interface SetSkyPreviewMessage {
  type: 'setSkyPreview';
  payload: SkyPreviewPayloadData;
}

/** Cache key for sceneSettingsStore.skyPreviews. Every input Unity rendered from, in a fixed order —
 *  change one of them and the modal asks for a fresh render instead of showing a stale sky. */
export function skyPreviewKey(sceneId: number, locationType: string, timeType: string, lightingMode: string, floorType: string): string {
  return `${sceneId}|${locationType}|${timeType}|${lightingMode}|${floorType}`;
}
export interface SceneDeletedPayload {
  sceneId: number;
  error: { messageKey: string } | null;
}
export interface SceneDeletedMessage {
  type: 'sceneDeleted';
  payload: SceneDeletedPayload;
}

/** Ack for the board scene context menu's insert/duplicate/omit verbs and the Add-scene row's
 * `addSceneToProject`.
 *
 * One verb with an `op` discriminator rather than four, to keep ALL_BRIDGE_TYPES small — the same
 * decision `shotMutated` made. `sceneId` is the NEW scene on a successful insert/duplicate/add, and
 * the REQUESTED scene on omit and on an insert/duplicate/omit failure; callers match on `op` plus
 * "this is the ack for the mutation I just started" (clear before send), never on sceneId, because
 * on success the new id is exactly what they are waiting to be told.
 *
 * `add` names no existing scene, so a failed add carries -1 (Unity's IllegalRecordId). Callers
 * ignore the id on an error ack. */
export type SceneMutationOp = 'insert' | 'duplicate' | 'omit' | 'add';
export interface SceneMutatedPayload {
  op: SceneMutationOp;
  sceneId: number;
  error: { messageKey: string } | null;
}
export interface SceneMutatedMessage {
  type: 'sceneMutated';
  payload: SceneMutatedPayload;
}

export type SceneSettingsMessage =
  | SetSceneSettingsMessage
  | SetFloorPreviewMessage
  | SetSkyPreviewMessage
  | SceneDeletedMessage
  | SceneMutatedMessage;

export const sceneSettingsHandlers = {
  setSceneSettings: (msg: SetSceneSettingsMessage) => {
    if (msg.payload && typeof msg.payload.sceneId === 'number') {
      useSceneSettingsStore.getState().setCurrent(msg.payload);
    }
  },
  setFloorPreview: (msg: SetFloorPreviewMessage) => {
    if (msg.payload && typeof msg.payload.floorType === 'string') {
      const { floorType, imageUrl } = msg.payload;
      useSceneSettingsStore.setState((s) => ({ floorPreviews: { ...s.floorPreviews, [floorType]: imageUrl } }));
    }
  },
  setSkyPreview: (msg: SetSkyPreviewMessage) => {
    if (msg.payload && typeof msg.payload.sceneId === 'number') {
      const { sceneId, locationType, timeType, lightingMode, floorType, imageUrl } = msg.payload;
      const key = skyPreviewKey(sceneId, locationType, timeType, lightingMode, floorType);
      useSceneSettingsStore.setState((s) => ({ skyPreviews: { ...s.skyPreviews, [key]: imageUrl } }));
    }
  },
  sceneDeleted: (msg: SceneDeletedMessage) => {
    if (msg.payload && typeof msg.payload.sceneId === 'number') {
      useSceneSettingsStore.getState().setDeleteResult(msg.payload);
    }
  },
  sceneMutated: (msg: SceneMutatedMessage) => {
    if (msg.payload && typeof msg.payload.op === 'string') {
      useSceneSettingsStore.getState().setMutateResult(msg.payload);
    }
  },
} satisfies HandlerMap;

export interface ApplySceneSettingsPayload {
  sceneId: number;
  sceneNumber: string;
  sceneName: string;
  sceneNotes: string;
  locationType: string;
  timeType: string;
  resetLightingToBasic: boolean;
  postProcessingKey: string;
  floorType: string;
  /**
   * 20260923 - true ONLY when the user explicitly picked a location in the Scene info dropdown this session
   * (sent only when true). Unity then links exactly the row named `sceneName` instead of inferring the link
   * from the text — which it must, because an unchanged text now keeps an UNLINKED scene unlinked (so the
   * per-scene trash and a location delete survive an untouched Done), and without this flag an unlinked
   * "BARN" could never be put back under BARN. Absent = false = the text rules.
   */
  locationPicked?: boolean;
}

export function sendRequestSceneSettings(sceneId: number): void {
  enqueueSendToNative('requestSceneSettings', { sceneId });
}
export function sendApplySceneSettings(payload: ApplySceneSettingsPayload): void {
  enqueueSendToNative('applySceneSettings', { ...payload });
}
export function sendRequestFloorPreview(floorType: string): void {
  enqueueSendToNative('requestFloorPreview', { floorType });
}
/** Asks Unity to render the scene's sky/lighting swatch.
 *
 *  locationType/timeType are the CANDIDATES the panel is showing — the header drawer's committed
 *  Type/Time, which have not been applied to the project yet. They are not optional in practice:
 *  Unity falls back to the record's PERSISTED values when they are absent and stamps the echo with
 *  those, so a request that omitted them after a drawer change would fill a cache key nobody is
 *  reading and leave the swatch on its placeholder for the rest of the panel's life. Omit them only
 *  when "whatever is persisted" is genuinely what you want rendered.
 *
 *  Floor is deliberately NOT a candidate: Unity always renders under rec.GetSceneFloorType(), so the
 *  cache keys on the echoed (persisted) floor rather than a pending pick.
 *
 *  lightingMode (20260902) is a candidate for the same reason location/time are: the panel can hold an
 *  armed "reset this scene's lighting to Basic" that Done has not written yet, and without it Unity
 *  renders — and echoes — the record's custom sky, so answering Yes changed nothing the user could see.
 *  Render-only: this verb persists nothing. Absent/empty keeps the old "use the record" behaviour. */
export function sendRequestSkyPreview(sceneId: number, locationType?: string, timeType?: string, lightingMode?: string): void {
  enqueueSendToNative('requestSkyPreview', { sceneId, locationType, timeType, lightingMode });
}
/** Hands off to Unity's native Custom Lighting editor for the scene.
 *
 *  20260902 - the four candidates are the Scene Settings panel's PENDING picks, mirroring the native
 *  panel seeding its own popup from panel-local values (SceneSettingsPopupPanel.cs:1923-1931). Additive
 *  and optional: an absent/empty field means "use the persisted record", so callers that only know a
 *  sceneId keep working unchanged. Nothing here is persisted — the popup's own Done is what writes. */
export function sendOpenLightingForScene(sceneId: number, locationType?: string, timeType?: string, floorType?: string, lightingMode?: string): void {
  enqueueSendToNative('openLightingForScene', { sceneId, locationType, timeType, floorType, lightingMode });
}
export function sendDeleteScene(sceneId: number): void {
  enqueueSendToNative('deleteScene', { sceneId });
}

/** Creates a scene with the board-add defaults directly AFTER `sceneId`. Unity acks the NEW scene id. */
export function sendInsertScene(sceneId: number): void {
  enqueueSendToNative('insertScene', { sceneId });
}
/** Duplicates `sceneId` (shots and objects included); the copy lands directly after the source.
 *  Unity acks the NEW scene id, and drops — with no ack at all — a second request arriving while
 *  one is still in flight, so the caller's busy overlay is the only guard against that. */
export function sendDuplicateScene(sceneId: number): void {
  enqueueSendToNative('duplicateScene', { sceneId });
}
/** Sets the scene's omitted flag to an EXPLICIT target state, never a blind toggle: a menu rendered
 *  against a pre-repush store must not be able to invert the wrong way. */
export function sendOmitScene(sceneId: number, omitted: boolean): void {
  enqueueSendToNative('omitScene', { sceneId, omitted });
}
